import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Grid,
  Chip,
  Switch,
  InputLabel,
  FormControl,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  VpnKey as PinIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { apiClient } from '@services/api/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useDashboardByOfficer } from '@hooks/useDashboard';

interface Officer {
  id: number;
  full_name: string;
  national_id: string;
  role: string;
  status: string;
  phone: string;
  last_login: string | null;
  daily_target?: number;
  allow_daily_total_submissions?: boolean;
}

const KPICard: React.FC<{
  title: string;
  value: string | number;
  color?: string;
}> = ({ title, value, color = '#FF6B4A' }) => (
  <Card sx={{ backgroundColor: '#1a2332', borderLeft: `4px solid ${color}` }}>
    <CardContent sx={{ p: 2 }}>
      <Typography sx={{ fontSize: '0.75rem', color: '#8b95a5', fontWeight: 500, mb: 1 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'white' }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
    </CardContent>
  </Card>
);

const SX_SELECT = {
  backgroundColor: '#0f1419',
  color: '#ffffff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a3444' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00D084' },
  '& .MuiSvgIcon-root': { color: '#8b95a5' },
};

const ROLE_CHOICES = [
  { value: 'CHIEF', label: 'Chief', sub: 'One location' },
  { value: 'CLERK', label: 'Clerk', sub: 'One location' },
];

/** The main API has no CLERK role: a clerk is a CHIEF there (same location
 *  and field-app access) and the add-on keeps the title (/field/titles/). */
const mainRole = (role: string) => (role === 'CLERK' ? 'CHIEF' : role);
const ROLE_LABELS: Record<string, string> = {
  CHIEF: 'Chief', CLERK: 'Clerk', DCC: 'DCC', ACC: 'ACC',
  REGISTRATION_OFFICER: 'Registration officer', SUPERVISOR: 'Supervisor', ADMINISTRATOR: 'Administrator',
};
const roleLabel = (role: string) => ROLE_LABELS[role] || role.replace(/_/g, ' ');

const setTitle = (officerId: number, role: string) =>
  apiClient.client.put(`/field/titles/${officerId}/`, { title: role === 'CLERK' ? 'CLERK' : '' });

// Static list — matches constants.KenyaCounties in the backend.
const KENYA_COUNTIES = [
  'BARINGO','BOMET','BUNGOMA','BUSIA','ELGEYO-MARAKWET','EMBU','GARISSA',
  'HOMABAY','ISIOLO','KAJIADO','KAKAMEGA','KERICHO','KIAMBU','KILIFI',
  'KIRINYAGA','KISII','KISUMU','KITUI','KWALE','LAIKIPIA','LAMU','MACHAKOS',
  'MAKUENI','MANDERA','MARSABIT','MERU','MIGORI','MOMBASA','MURANGA',
  'NAIROBI','NAKURU','NANDI','NAROK','NYANDARUA','NYAMIRA','NYERI',
  'SAMBURU','SIAYA','TAITA/TAVETA','TANA RIVER','THARAKA NITHI',
  'TRANS NZOIA','TURKANA','UASIN GISHU','VIHIGA','WAJIR','WEST POKOT',
].sort();

export const OfficersPage: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOfficer, setSelectedOfficer] = useState<Officer | null>(null);
  const [issuePinDialog, setIssuePinDialog] = useState(false);
  const [issuedPin, setIssuedPin] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // ── Add Officer dialog state ──────────────────────────────────────────
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addCreatedPin, setAddCreatedPin] = useState<string | null>(null);
  const [newOfficer, setNewOfficer] = useState({
    national_id: '',
    full_name: '',
    phone: '+254',
    daily_target: 40,
    role: 'CHIEF',
    county: '',
    district: '',
    division: '',
    location: '',
    allow_daily_total_submissions: true,
  });

  // Cascade dropdowns: districts → divisions → locations (counties are static)
  const districtsQuery = useQuery({
    queryKey: ['districts', newOfficer.county],
    queryFn: async () => {
      const r = await apiClient.client.get('/citizens/districts/', { params: { county: newOfficer.county } });
      return r.data as string[];
    },
    enabled: addDialogOpen && !!newOfficer.county,
  });

  const divisionsQuery = useQuery({
    queryKey: ['divisions', newOfficer.county, newOfficer.district],
    queryFn: async () => {
      const r = await apiClient.client.get('/citizens/divisions/', {
        params: { county: newOfficer.county, district: newOfficer.district },
      });
      return r.data as string[];
    },
    enabled: addDialogOpen && !!newOfficer.county && !!newOfficer.district,
  });

  const locationsQuery = useQuery({
    queryKey: ['locations', newOfficer.county, newOfficer.district, newOfficer.division],
    queryFn: async () => {
      const r = await apiClient.client.get('/citizens/locations/', {
        params: { county: newOfficer.county, district: newOfficer.district, division: newOfficer.division },
      });
      return r.data as string[];
    },
    enabled: addDialogOpen && !!newOfficer.county && !!newOfficer.district && !!newOfficer.division,
  });

  const setField = (field: string, value: any) => {
    setNewOfficer((prev) => {
      const updated = { ...prev, [field]: value };
      // Reset downstream when parent changes
      if (field === 'county') { updated.district = ''; updated.division = ''; updated.location = ''; }
      if (field === 'district') { updated.division = ''; updated.location = ''; }
      if (field === 'division') { updated.location = ''; }
      return updated;
    });
  };

  const createOfficerMutation = useMutation({
    mutationFn: async (data: typeof newOfficer) => {
      const r = await apiClient.client.post('/officers/', { ...data, role: mainRole(data.role) });
      if (data.role === 'CLERK') await setTitle(r.data.id, 'CLERK');
      return r.data;
    },
    onSuccess: (data) => {
      setAddCreatedPin(data.pin);
      queryClient.invalidateQueries({ queryKey: ['officers'] });
    },
    onError: (err: any) => {
      alert(err.response?.data?.error?.message || 'Failed to create officer');
    },
  });

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setAddCreatedPin(null);
    setNewOfficer({
      national_id: '', full_name: '', phone: '+254', daily_target: 40,
      role: 'CHIEF', county: '', district: '', division: '', location: '',
      allow_daily_total_submissions: true,
    });
  };

  // ── Edit Officer state ─────────────────────────────────────────────
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editOfficer, setEditOfficer] = useState<Officer | null>(null);
  const [editForm, setEditForm] = useState({ full_name: '', phone: '', role: '', status: '', daily_target: 40 });

  const openEdit = (officer: Officer) => {
    setEditOfficer(officer);
    setEditForm({ full_name: officer.full_name, phone: officer.phone, role: displayRole(officer), status: officer.status, daily_target: officer.daily_target || 40 });
    setEditDialogOpen(true);
  };

  const editMutation = useMutation({
    mutationFn: async (data: typeof editForm) => {
      const r = await apiClient.client.patch(`/officers/${editOfficer!.id}/`, { ...data, role: mainRole(data.role) });
      if (mainRole(data.role) === 'CHIEF' && data.role !== displayRole(editOfficer!)) {
        await setTitle(editOfficer!.id, data.role);
      }
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['officers'] });
      setEditDialogOpen(false);
    },
    onError: (err: any) => alert(err.response?.data?.error?.message || 'Update failed'),
  });

  // ── Delete Officer state ───────────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Officer | null>(null);

  const openDelete = (officer: Officer) => { setDeleteTarget(officer); setDeleteDialogOpen(true); };

  const deleteMutation = useMutation({
    mutationFn: async (officerId: number) => {
      await apiClient.client.delete(`/officers/${officerId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['officers'] });
      setDeleteDialogOpen(false);
    },
    onError: (err: any) => alert(err.response?.data?.error?.message || 'Delete failed'),
  });

  // Fetch officers
  const officersQuery = useQuery({
    queryKey: ['officers', selectedRole, selectedStatus],
    queryFn: async () => {
      const response = await apiClient.client.get('/officers/', {
        params: {
          role: selectedRole !== 'all' ? mainRole(selectedRole) : undefined,
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
        },
      });
      return response.data.results || response.data;
    },
  });

  // Which chiefs are clerks. Without it (add-on down) everyone shows as stored.
  const titlesQuery = useQuery({
    queryKey: ['officers', 'titles'],
    queryFn: async () => (await apiClient.client.get('/field/titles/')).data as Record<string, string>,
  });
  function displayRole(o: Officer) {
    return o.role === 'CHIEF' && titlesQuery.data?.[String(o.id)] === 'CLERK' ? 'CLERK' : o.role;
  }

  // Issue PIN mutation
  const issuePinMutation = useMutation({
    mutationFn: async (officerId: number) => {
      const response = await apiClient.client.post(`/officers/${officerId}/issue_pin/`);
      return response.data;
    },
    onSuccess: (data) => {
      setIssuedPin(data.pin);
      queryClient.invalidateQueries({ queryKey: ['officers'] });
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || 'Failed to issue PIN');
    },
  });

  const handleIssuePIN = (officer: Officer) => {
    setSelectedOfficer(officer);
    setIssuePinDialog(true);
  };

  const handleConfirmIssuePIN = () => {
    if (selectedOfficer) {
      issuePinMutation.mutate(selectedOfficer.id);
    }
  };

  const handleCopyPin = () => {
    if (issuedPin) {
      navigator.clipboard.writeText(issuedPin);
      alert('PIN copied to clipboard');
    }
  };

  const handleClosePinDialog = () => {
    setIssuePinDialog(false);
    setIssuedPin(null);
    setSelectedOfficer(null);
  };

  const filteredOfficers = (officersQuery.data || []).filter((officer: Officer) =>
    (selectedRole === 'all' || displayRole(officer) === selectedRole) &&
    (officer.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      officer.national_id.includes(searchQuery))
  );

  const officerStatsQuery = useDashboardByOfficer();
  const appTodayById = new Map<number, number>(
    (officerStatsQuery.data || []).map((s: any) => [s.officer_id, s.registrations_today || 0]),
  );
  // People registered on paper today (field app "Daily total"), from the add-on.
  const paperTodayQuery = useQuery({
    queryKey: ['officers', 'daily-totals'],
    queryFn: async () => (await apiClient.client.get('/field/daily-totals/')).data as Record<string, number>,
    refetchInterval: 60_000,
  });
  const paperToday = (id: number) => paperTodayQuery.data?.[String(id)] || 0;
  const registrationsTodayById = {
    get: (id: number) => (appTodayById.get(id) || 0) + paperToday(id),
  };

  const stats = {
    total: officersQuery.data?.length || 0,
    active: (officersQuery.data || []).filter((o: Officer) => o.status === 'ACTIVE').length,
    clerks: (officersQuery.data || []).filter((o: Officer) => displayRole(o) === 'CLERK').length,
    met_target: (officersQuery.data || []).filter(
      (o: Officer) => (registrationsTodayById.get(o.id) || 0) >= (o.daily_target || 40),
    ).length,
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* KPI Cards */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Total Officers" value={stats.total} color="#00D084" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Active" value={stats.active} color="#00B8D4" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Clerks" value={stats.clerks} color="#FFA500" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Met Target" value={stats.met_target} color="#9C27B0" />
        </Grid>
      </Grid>

      {/* Filters + Add Officer button */}
      <Card sx={{ backgroundColor: '#1a2332', mb: 2 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              backgroundColor: '#0f1419',
              flex: 1,
              minWidth: 200,
              '& .MuiOutlinedInput-root': {
                color: '#8b95a5',
                '& fieldset': { borderColor: '#2a3444' },
              },
            }}
          />
          <Select
            size="small"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            sx={{
              backgroundColor: '#0f1419',
              color: '#8b95a5',
              minWidth: 150,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a3444' },
            }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            {ROLE_CHOICES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </Select>
          <Select
            size="small"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            sx={{
              backgroundColor: '#0f1419',
              color: '#8b95a5',
              minWidth: 120,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a3444' },
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
            <MenuItem value="SUSPENDED">Suspended</MenuItem>
          </Select>

          {/* Add Officer button */}
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setAddDialogOpen(true)}
            size="small"
            sx={{
              backgroundColor: '#00D084',
              color: '#0f1419',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#00B870' },
              whiteSpace: 'nowrap',
            }}
          >
            + Add officer
          </Button>
        </CardContent>
      </Card>

      {/* Officers Table */}
      <Card sx={{ backgroundColor: '#1a2332' }}>
        <CardContent sx={{ p: 0 }}>
          {officersQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : officersQuery.error ? (
            <Alert severity="error">Failed to load officers</Alert>
          ) : filteredOfficers.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#2a3444' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#8b95a5' }}>Officer</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#8b95a5' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#8b95a5' }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#8b95a5' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#8b95a5' }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#8b95a5', width: 160 }}>Today's Progress</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#8b95a5' }}>Last Login</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#8b95a5' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOfficers.map((officer: Officer) => (
                    <TableRow key={officer.id} sx={{ borderBottom: '1px solid #2a3444', '&:hover': { backgroundColor: '#252d3d' } }}>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'white', fontWeight: 500 }}>
                        {officer.full_name}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: '#8b95a5' }}>
                        {officer.national_id}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: '#8b95a5' }}>
                        {roleLabel(displayRole(officer))}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        <Chip
                          label={officer.status}
                          size="small"
                          sx={{
                            backgroundColor: officer.status === 'ACTIVE' ? '#00D084' : '#FFA500',
                            color: '#0f1419',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: '#8b95a5' }}>
                        {officer.phone}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const target = officer.daily_target || 40;
                          const actual = registrationsTodayById.get(officer.id) || 0;
                          const paper = paperToday(officer.id);
                          const pct = Math.min(100, Math.round((actual / target) * 100));
                          return (
                            <Box
                              title={paper > 0 ? `${actual - paper} in the app + ${paper} on paper` : undefined}
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                              <Box sx={{ flex: 1, height: 4, backgroundColor: '#0f1419', borderRadius: 0.5, position: 'relative' }}>
                                <Box sx={{
                                  position: 'absolute', left: 0, top: 0, bottom: 0,
                                  width: `${pct}%`, borderRadius: 0.5,
                                  backgroundColor: pct >= 100 ? '#00D084' : pct > 0 ? '#FFA500' : '#2a3444',
                                }} />
                              </Box>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: pct >= 100 ? '#00D084' : '#8b95a5', minWidth: 62, textAlign: 'right' }}>
                                {actual}/{target}
                              </Typography>
                              {paper > 0 && (
                                <Typography sx={{ fontSize: '0.65rem', color: '#FFA500', whiteSpace: 'nowrap' }}>
                                  {paper} paper
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: '#8b95a5' }}>
                        {officer.last_login ? new Date(officer.last_login).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleIssuePIN(officer)} sx={{ color: '#00D084' }} title="Issue PIN">
                          <PinIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => openEdit(officer)} sx={{ color: '#00B8D4' }} title="Edit officer">
                          <EditIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => openDelete(officer)} sx={{ color: '#FF4444' }} title="Delete officer">
                          <DeleteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ p: 3, color: '#8b95a5', textAlign: 'center' }}>
              No officers found
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Issue PIN Dialog */}
      <Dialog open={issuePinDialog} onClose={handleClosePinDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1a2332', color: 'white' }}>
          {issuedPin ? 'PIN Generated' : 'Issue PIN'}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0f1419', color: 'white', pt: 2 }}>
          {issuedPin ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: '#8b95a5' }}>
                New PIN for {selectedOfficer?.full_name}:
              </Typography>
              <TextField
                fullWidth
                value={issuedPin}
                variant="outlined"
                sx={{
                  mb: 2,
                  backgroundColor: '#1a2332',
                  '& .MuiOutlinedInput-root': { color: '#00D084', fontWeight: 700, fontSize: '1.2rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a3444' },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopyPin} size="small" sx={{ color: '#00D084' }}>
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                Share this PIN securely with the officer. It will not be displayed again.
              </Alert>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#8b95a5' }}>
              Are you sure you want to issue a new PIN to {selectedOfficer?.full_name}?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1a2332', p: 1.5 }}>
          <Button onClick={handleClosePinDialog} sx={{ color: '#8b95a5' }}>
            {issuedPin ? 'Done' : 'Cancel'}
          </Button>
          {!issuedPin && (
            <Button
              onClick={handleConfirmIssuePIN}
              variant="contained"
              sx={{
                backgroundColor: '#00D084',
                color: '#0f1419',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#00B870' },
              }}
              disabled={issuePinMutation.isPending}
            >
              {issuePinMutation.isPending ? 'Issuing...' : 'Issue PIN'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Add Officer Dialog ───────────────────────────────────────── */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#1a2332', color: 'white' } }}
      >
        <DialogTitle sx={{ backgroundColor: '#1a2332', color: 'white', borderBottom: '1px solid #2a3444' }}>
          {addCreatedPin ? 'Officer Created — Save PIN' : 'Add Officer'}
          {!addCreatedPin && (
            <Typography variant="caption" sx={{ display: 'block', color: '#8b95a5', mt: 0.5 }}>
              They sign in to the field app with their ID number and the 6-digit PIN.
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: '#0f1419', pt: 2 }}>
          {addCreatedPin ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Officer created successfully. Share this PIN securely — it will not be shown again.
              </Alert>
              <TextField
                fullWidth
                label="Generated PIN"
                value={addCreatedPin}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': { color: '#00D084', fontWeight: 700, fontSize: '1.4rem', backgroundColor: '#1a2332' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00D084' },
                  '& .MuiInputLabel-root': { color: '#8b95a5' },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => { navigator.clipboard.writeText(addCreatedPin); }}
                        size="small"
                        sx={{ color: '#00D084' }}
                      >
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ID Number — Used as Login"
                    helperText="4-9 digits · must be unique"
                    value={newOfficer.national_id}
                    onChange={(e) => setField('national_id', e.target.value.replace(/\D/, ''))}
                    inputProps={{ maxLength: 9 }}
                    sx={{ '& .MuiInputLabel-root': { color: '#8b95a5' }, '& .MuiOutlinedInput-root': { color: '#fff', backgroundColor: '#1a2332', '& fieldset': { borderColor: '#2a3444' } }, '& .MuiFormHelperText-root': { color: '#8b95a5' } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Full Name"
                    value={newOfficer.full_name}
                    onChange={(e) => setField('full_name', e.target.value)}
                    sx={{ '& .MuiInputLabel-root': { color: '#8b95a5' }, '& .MuiOutlinedInput-root': { color: '#fff', backgroundColor: '#1a2332', '& fieldset': { borderColor: '#2a3444' } } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone Number"
                    helperText="For contact — no SMS is sent"
                    value={newOfficer.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    sx={{ '& .MuiInputLabel-root': { color: '#8b95a5' }, '& .MuiOutlinedInput-root': { color: '#fff', backgroundColor: '#1a2332', '& fieldset': { borderColor: '#2a3444' } }, '& .MuiFormHelperText-root': { color: '#8b95a5' } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Daily Target"
                    helperText="Suggested from this location's pool"
                    value={newOfficer.daily_target}
                    onChange={(e) => setField('daily_target', parseInt(e.target.value) || 40)}
                    inputProps={{ min: 1, max: 500 }}
                    sx={{ '& .MuiInputLabel-root': { color: '#8b95a5' }, '& .MuiOutlinedInput-root': { color: '#fff', backgroundColor: '#1a2332', '& fieldset': { borderColor: '#2a3444' } }, '& .MuiFormHelperText-root': { color: '#8b95a5' } }}
                  />
                </Grid>
              </Grid>

              {/* Role picker */}
              <Box>
                <Typography variant="caption" sx={{ color: '#8b95a5', mb: 1, display: 'block' }}>ROLE</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {ROLE_CHOICES.map((r) => (
                    <Box
                      key={r.value}
                      onClick={() => setField('role', r.value)}
                      sx={{
                        px: 2, py: 1, borderRadius: '6px', cursor: 'pointer', flex: 1, minWidth: 70,
                        textAlign: 'center',
                        border: `2px solid ${newOfficer.role === r.value ? '#00D084' : '#2a3444'}`,
                        backgroundColor: newOfficer.role === r.value ? '#00D08422' : '#1a2332',
                        transition: 'all 0.15s',
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: newOfficer.role === r.value ? '#00D084' : '#fff' }}>
                        {r.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#8b95a5', fontSize: '0.65rem' }}>{r.sub}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Assignment heading */}
              <Box>
                <Typography variant="caption" sx={{ color: '#8b95a5', display: 'block', mb: 1 }}>
                  ASSIGNMENT {newOfficer.county && `${newOfficer.county}`}{newOfficer.district && ` › ${newOfficer.district}`}{newOfficer.division && ` › ${newOfficer.division}`}
                </Typography>

                {/* County */}
                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                  <InputLabel sx={{ color: '#8b95a5' }}>County</InputLabel>
                  <Select
                    value={newOfficer.county}
                    onChange={(e) => setField('county', e.target.value)}
                    label="County"
                    sx={{ ...SX_SELECT, backgroundColor: '#1a2332' }}
                    MenuProps={{ PaperProps: { sx: { backgroundColor: '#1a2332', color: '#fff' } } }}
                  >
                    <MenuItem value="">— Select —</MenuItem>
                    {KENYA_COUNTIES.map((c) => (
                      <MenuItem key={c} value={c} sx={{ '&:hover': { backgroundColor: '#252d3d' } }}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* District */}
                <FormControl fullWidth size="small" sx={{ mb: 1.5 }} disabled={!newOfficer.county || districtsQuery.isLoading}>
                  <InputLabel sx={{ color: '#8b95a5' }}>
                    {districtsQuery.isLoading ? 'Loading…' : 'District'}
                  </InputLabel>
                  <Select
                    value={newOfficer.district}
                    onChange={(e) => setField('district', e.target.value)}
                    label={districtsQuery.isLoading ? 'Loading…' : 'District'}
                    sx={{ ...SX_SELECT, backgroundColor: '#1a2332' }}
                    MenuProps={{ PaperProps: { sx: { backgroundColor: '#1a2332', color: '#fff' } } }}
                  >
                    <MenuItem value="">— Select —</MenuItem>
                    {(districtsQuery.data || []).map((d) => (
                      <MenuItem key={d} value={d} sx={{ '&:hover': { backgroundColor: '#252d3d' } }}>{d}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Division */}
                {(newOfficer.role === 'ACC' || mainRole(newOfficer.role) === 'CHIEF' || newOfficer.role === 'REGISTRATION_OFFICER') && (
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }} disabled={!newOfficer.district || divisionsQuery.isLoading}>
                    <InputLabel sx={{ color: '#8b95a5' }}>
                      {divisionsQuery.isLoading ? 'Loading…' : 'Division'}
                    </InputLabel>
                    <Select
                      value={newOfficer.division}
                      onChange={(e) => setField('division', e.target.value)}
                      label={divisionsQuery.isLoading ? 'Loading…' : 'Division'}
                      sx={{ ...SX_SELECT, backgroundColor: '#1a2332' }}
                      MenuProps={{ PaperProps: { sx: { backgroundColor: '#1a2332', color: '#fff' } } }}
                    >
                      <MenuItem value="">— Select —</MenuItem>
                      {(divisionsQuery.data || []).map((d) => (
                        <MenuItem key={d} value={d} sx={{ '&:hover': { backgroundColor: '#252d3d' } }}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Location */}
                {mainRole(newOfficer.role) === 'CHIEF' && (
                  <FormControl fullWidth size="small" sx={{ mb: 1 }} disabled={!newOfficer.division || locationsQuery.isLoading}>
                    <InputLabel sx={{ color: '#8b95a5' }}>
                      {locationsQuery.isLoading ? 'Loading…' : 'Location'}
                    </InputLabel>
                    <Select
                      value={newOfficer.location}
                      onChange={(e) => setField('location', e.target.value)}
                      label={locationsQuery.isLoading ? 'Loading…' : 'Location'}
                      sx={{ ...SX_SELECT, backgroundColor: '#1a2332' }}
                      MenuProps={{ PaperProps: { sx: { backgroundColor: '#1a2332', color: '#fff' } } }}
                    >
                      <MenuItem value="">— Select —</MenuItem>
                      {(locationsQuery.data || []).map((l) => (
                        <MenuItem key={l} value={l} sx={{ '&:hover': { backgroundColor: '#252d3d' } }}>{l}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>

              {/* Allow daily total toggle */}
              <Box
                sx={{
                  p: 1.5, borderRadius: '6px', border: '1px solid #2a3444',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                    Allow daily-total submissions
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#8b95a5' }}>
                    One figure instead of ticking each ID. Totals stay pending until an ACC verifies them.
                  </Typography>
                </Box>
                <Switch
                  checked={newOfficer.allow_daily_total_submissions}
                  onChange={(e) => setField('allow_daily_total_submissions', e.target.checked)}
                  sx={{ '& .MuiSwitch-thumb': { backgroundColor: newOfficer.allow_daily_total_submissions ? '#00D084' : '#8b95a5' } }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ backgroundColor: '#1a2332', p: 1.5, borderTop: '1px solid #2a3444' }}>
          <Button onClick={handleCloseAddDialog} sx={{ color: '#8b95a5' }}>
            {addCreatedPin ? 'Done' : 'Cancel'}
          </Button>
          {!addCreatedPin && (
            <Button
              variant="contained"
              onClick={() => createOfficerMutation.mutate(newOfficer)}
              disabled={createOfficerMutation.isPending || !newOfficer.national_id || !newOfficer.full_name}
              sx={{ backgroundColor: '#00D084', color: '#0f1419', fontWeight: 700, '&:hover': { backgroundColor: '#00B870' } }}
            >
              {createOfficerMutation.isPending ? 'Creating…' : 'Create officer'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Edit Officer Dialog ─────────────────────────────────────── */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { backgroundColor: '#1a2332', color: 'white' } }}>
        <DialogTitle sx={{ backgroundColor: '#1a2332', borderBottom: '1px solid #2a3444', color: 'white' }}>
          Edit Officer — {editOfficer?.full_name}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0f1419', pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField fullWidth size="small" label="Full Name" value={editForm.full_name}
              onChange={e => setEditForm(p => ({ ...p, full_name: e.target.value }))}
              sx={{ '& .MuiInputLabel-root': { color: '#8b95a5' }, '& .MuiOutlinedInput-root': { color: '#fff', backgroundColor: '#1a2332', '& fieldset': { borderColor: '#2a3444' } } }} />
            <TextField fullWidth size="small" label="Phone" value={editForm.phone}
              onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))}
              sx={{ '& .MuiInputLabel-root': { color: '#8b95a5' }, '& .MuiOutlinedInput-root': { color: '#fff', backgroundColor: '#1a2332', '& fieldset': { borderColor: '#2a3444' } } }} />
            <TextField fullWidth size="small" type="number" label="Daily Target" value={editForm.daily_target}
              onChange={e => setEditForm(p => ({ ...p, daily_target: parseInt(e.target.value) || 40 }))}
              sx={{ '& .MuiInputLabel-root': { color: '#8b95a5' }, '& .MuiOutlinedInput-root': { color: '#fff', backgroundColor: '#1a2332', '& fieldset': { borderColor: '#2a3444' } } }} />
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: '#8b95a5' }}>Role</InputLabel>
              <Select value={editForm.role} onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))}
                label="Role" sx={{ ...SX_SELECT, backgroundColor: '#1a2332' }}
                MenuProps={{ PaperProps: { sx: { backgroundColor: '#1a2332', color: '#fff' } } }}>
                {ROLE_CHOICES.map(r => <MenuItem key={r.value} value={r.value}>{r.label} — {r.sub}</MenuItem>)}
                {/* Older accounts (DCC, ACC, …) keep their role unless changed. */}
                {!ROLE_CHOICES.some(r => r.value === editForm.role) && editForm.role && (
                  <MenuItem value={editForm.role}>{roleLabel(editForm.role)}</MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: '#8b95a5' }}>Status</InputLabel>
              <Select value={editForm.status} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}
                label="Status" sx={{ ...SX_SELECT, backgroundColor: '#1a2332' }}
                MenuProps={{ PaperProps: { sx: { backgroundColor: '#1a2332', color: '#fff' } } }}>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
                <MenuItem value="SUSPENDED">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1a2332', borderTop: '1px solid #2a3444', p: 1.5 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: '#8b95a5' }}>Cancel</Button>
          <Button variant="contained" onClick={() => editMutation.mutate(editForm)}
            disabled={editMutation.isPending || !editForm.full_name}
            sx={{ backgroundColor: '#00B8D4', color: '#0f1419', fontWeight: 700, '&:hover': { backgroundColor: '#0098b0' } }}>
            {editMutation.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Officer Dialog ───────────────────────────────────── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { backgroundColor: '#1a2332', color: 'white' } }}>
        <DialogTitle sx={{ backgroundColor: '#1a2332', color: 'white', borderBottom: '1px solid #2a3444' }}>
          Delete Officer
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0f1419', pt: 2 }}>
          <Typography variant="body2" sx={{ color: '#8b95a5' }}>
            Are you sure you want to permanently delete <strong style={{ color: 'white' }}>{deleteTarget?.full_name}</strong>?
          </Typography>
          <Typography variant="caption" sx={{ color: '#FF4444', display: 'block', mt: 1 }}>
            This will also delete all their assigned locations and audit logs. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1a2332', borderTop: '1px solid #2a3444', p: 1.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#8b95a5' }}>Cancel</Button>
          <Button variant="contained" onClick={() => deleteMutation.mutate(deleteTarget!.id)}
            disabled={deleteMutation.isPending}
            sx={{ backgroundColor: '#FF4444', color: 'white', fontWeight: 700, '&:hover': { backgroundColor: '#cc3333' } }}>
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
